import isObject from 'lodash/isObject';
import { MarkerType } from 'reactflow';

import { FlowType } from '@ply-io/ply-front-shared-utilities';

import { MODULE_NAME } from './const';
import { getWorkflow } from './getters';
import {
	SET_WORKFLOW_DATA,
	REMOVE_WORKFLOW,
	ADD_NODE_TO_WORKFLOW_NODES,
	SET_WORKFLOW_SHARED_DATA,
	SET_WORKFLOW_SHARED_DATA_LOADING,
	SET_APPS_WORKSPACES,
	SET_SHARED_WORKFLOWS,
	SET_WAITING_FOR_SAMPLE_DATA,
	SET_UPDATING_WORKFLOW,
	SET_IS_LOADING_WORKFLOW_GRAPH,
} from './mutations';
import state from './state';
import StoreModuleService from '../StoreModuleService';
import { getInstallable, setInstallable } from '../installables/actions';
import { setLoading } from '../main/actions';
import { setNativeElementData } from '../nativeElements/actions';
import { deleteNodeData, bulkSetNodesData, fetchNodesSideEffectsDataByIds } from '../nodes/actions';
import { removeWorkflowIdFromProject, addWorkflowIdToProject } from '../projects/actions';
import { projectsList } from '../projects/getters';
import { fetchSubflowParam as fetchSubflowParams } from '../subflows/actions';
import { setUiElementData } from '../uiElements/actions';
import { getConfiguration, setConfigurationData } from '../workflowConfigurations/actions';
import { getVersionOfWorkflow } from '../workflows/modules/versions/actions';
import api from '@/api';
import { EdgeTypes } from '@/components/FlowTree/flow-tree-service.const';
import { getAccountMembers } from '@/store/modules/account/actions';
import { setEdgesData, clearEdges } from '@/store/modules/edges/actions';
import WorkflowService from '@/utils/WorkflowService';
import ExtensionCommunicationService from '@/utils/extension-communication-service';
import { isNullOrUndefined } from '@/utils/helpers';
import ProjectService from '@/utils/projects-service';
import { ALL_PROJECTS_VIRTUAL_ID, PROJECTS_SHARED_WITH_ME_VIRTUAL_ID } from '@/utils/projects.const';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import { WAITING_FLAG_TYPES } from '@/utils/workflow-service.const';

const buildWorkflowActionObject = (action) => StoreModuleService.buildActionObject(MODULE_NAME, action);

// actions declarations
export const reset = buildWorkflowActionObject('reset'); // the handler created in createDefaultActions
export const setWorkflowData = buildWorkflowActionObject('setWorkflowData');
export const setAllWorkflowData = buildWorkflowActionObject('setAllWorkflowData');
export const getWorkflowData = buildWorkflowActionObject('getWorkflowData');
export const getWorkflows = buildWorkflowActionObject('getWorkflows');
export const getWorkflowsForCredentials = buildWorkflowActionObject('getWorkflowsForCredentials');
export const createWorkflow = buildWorkflowActionObject('createWorkflow');
export const createSubflowFromExistingNodes = buildWorkflowActionObject('createSubflowFromExistingNodes');
export const createModule = buildWorkflowActionObject('createModule');
export const deleteWorkflow = buildWorkflowActionObject('deleteWorkflow');
export const deleteWorkflowFromStore = buildWorkflowActionObject('deleteWorkflowFromStore');
export const publishWorkflow = buildWorkflowActionObject('publishWorkflow');
export const updateWorkflow = buildWorkflowActionObject('updateWorkflow');
export const handleWorkflowUpdated = buildWorkflowActionObject('handleWorkflowUpdated');
export const shareWorkflow = buildWorkflowActionObject('shareWorkflow');
export const addNodeToWorkflowNodes = buildWorkflowActionObject('addNodeToWorkflowNodes');
export const toggleWorkflowIsOnSettings = buildWorkflowActionObject('toggleWorkflowIsOnSettings');
export const changeGroupFeatureVisibility = buildWorkflowActionObject('changeGroupFeatureVisibility');
export const applyExistingWorkflowVisibilityToAllGroupMembers = buildWorkflowActionObject(
	'applyExistingWorkflowVisibilityToAllGroupMembers'
);
export const setWorkflowShareData = buildWorkflowActionObject('setWorkflowShareData');
export const updateWorkflowUserRole = buildWorkflowActionObject('updateWorkflowUserRole');
export const updateWorkflowGroupRole = buildWorkflowActionObject('updateWorkflowGroupRole');
export const restoreWorkflowVersionAsDraft = buildWorkflowActionObject('restoreWorkflowVersionAsDraft');
export const createConfigurationFieldForWorkflow = buildWorkflowActionObject('createConfigurationField');
export const createInstallableForWorkflow = buildWorkflowActionObject('createInstallableForWorkflow');
export const markWorkflowSetupComplete = buildWorkflowActionObject('markWorkflowSetupComplete');
export const getAppsWorkspaces = buildWorkflowActionObject('getAppsWorkspaces');
export const moveWorkflowToOtherProject = buildWorkflowActionObject('moveWorkflowToOtherProject');
export const testWorkflow = buildWorkflowActionObject('testWorkflow');
export const updateWorkflowIsActive = buildWorkflowActionObject('updateWorkflowIsActive');
export const duplicateWorkflow = buildWorkflowActionObject('duplicateWorkflow');
export const setAllWorkflowsData = buildWorkflowActionObject('setAllWorkflowsData');
export const setWaitingForSampleData = buildWorkflowActionObject('setWaitingForSampleData');
export const fetchIsWaitingForSampleData = buildWorkflowActionObject('fetchIsWaitingForSampleData');
export const syncWorkflowVersionNodes = buildWorkflowActionObject('syncWorkflowVersionNodes');
export const updateWorkflowDraftPendingChangesFlag = buildWorkflowActionObject('updateWorkflowDraftPendingChangesFlag');
export const clearVersionFromWorkflow = buildWorkflowActionObject('clearVersionFromWorkflow');
export const updateVersionOfWorkflow = buildWorkflowActionObject('updateVersionOfWorkflow');
export const changeWorkflowUserSettings = buildWorkflowActionObject('changeWorkflowUserSettings');
export const getWorkflowVersionGraph = buildWorkflowActionObject('getWorkflowVersionGraph');

const actions = {
	...StoreModuleService.createDefaultActions(),
	async [getWorkflowData.action]({ dispatch, getters }, payload) {
		const { id, forceGet, needFetchNodes, forceGetNodes, getSubFlowConfigurationParams, getWorkflowWaitingFlag } = payload;
		const dataIsMissing = isNullOrUndefined(state.data[id]);
		const needToGet = dataIsMissing || forceGet;
		if (needToGet) {
			const ret = await api.getWorkflow(id);
			await dispatch(setAllWorkflowData.action, {
				data: ret,
				forceGetNodes,
				needFetchNodes,
				getSubFlowConfigurationParams,
				getWorkflowWaitingFlag,
			});
			// Relaxing the time when we get workflow share data. might cause regerssion so leaving this note to bring it back if needed
			// We will still need to find a way not to run it EVERY time we go and bring workflow node data
			// dispatch(setWorkflowShareData.action, { id, force: true });
		}
		return getters[getWorkflow.getter](id);
	},
	[setWorkflowData.action]({ commit }, payload) {
		const { data: workflow } = payload;
		commit(SET_WORKFLOW_DATA, { key: workflow.id, value: workflow });
	},
	[clearVersionFromWorkflow.action]({ commit, getters }, { workflowId, workflowVersionId }) {
		const workflow = { ...getters[getWorkflow.getter](workflowId) };
		const newWorkflowVersions = workflow.versions.filter((version) => version.id !== workflowVersionId);
		const newWorkflow = { ...workflow, versions: newWorkflowVersions };
		commit(SET_WORKFLOW_DATA, { key: workflow.id, value: newWorkflow });
	},
	[updateVersionOfWorkflow.action]({ commit, getters }, { workflowId, workflowVersion }) {
		const workflow = { ...getters[getWorkflow.getter](workflowId) };
		let workflowVersions = [...workflow.versions];
		const workflowVersionCurrentIndex = workflowVersions.findIndex((version) => version.id === workflowVersion.id);
		if (workflowVersionCurrentIndex === -1) {
			workflowVersions = [...workflowVersions, workflowVersion];
		} else {
			workflowVersions[workflowVersionCurrentIndex] = workflowVersion;
		}
		const newWorkflow = { ...workflow, versions: workflowVersions };
		if (workflowVersion.id === newWorkflow.current_draft.id) {
			newWorkflow.current_draft = workflowVersion;
		}
		commit(SET_WORKFLOW_DATA, { key: workflow.id, value: newWorkflow });
	},
	[updateWorkflowDraftPendingChangesFlag.action]({ commit, getters }, { value, workflowId }) {
		const workflow = getters[getWorkflow.getter](workflowId);
		const newWorkflow = { ...workflow, draft_pending_changes_flag: value };
		commit(SET_WORKFLOW_DATA, { key: workflow.id, value: newWorkflow });
	},
	[syncWorkflowVersionNodes.action]({ commit, getters }, payload) {
		const { workflowId, workflowVersionId, nodeId, remove } = payload;
		const workflow = getters[getWorkflow.getter](workflowId);
		const workflowCurrentDraft = workflow.current_draft;
		const isVersionCurrentDraft = workflowCurrentDraft.id === workflowVersionId;
		if (isVersionCurrentDraft) {
			// For now, we update only for current draft
			const currentDraftNodesIds = workflowCurrentDraft.nodes;
			let newNodesIds = [...currentDraftNodesIds];
			if (remove) {
				newNodesIds = newNodesIds.filter((id) => id !== nodeId);
			} else {
				newNodesIds.push(nodeId);
			}
			const newUniqueNodeIds = [...new Set(newNodesIds)];
			const newWorkflowCurrentDraft = { ...workflowCurrentDraft, nodes: newUniqueNodeIds };
			const newWorkflow = { ...workflow, current_draft: newWorkflowCurrentDraft };
			commit(SET_WORKFLOW_DATA, { key: workflowId, value: newWorkflow });
		}
	},

	async [getAppsWorkspaces.action]({ commit }, payload) {
		const isDataMissing = state.appsWorkspaces.length === 0;
		const needToGet = payload?.forceGet || isDataMissing;
		if (needToGet) {
			const appsWorkspaces = await api.getAppsWorkspaces();
			commit(SET_APPS_WORKSPACES, { appsWorkspaces });
		}
	},

	[markWorkflowSetupComplete.action]({ dispatch, getters }, payload) {
		const workflowId = payload.id;
		const workflowData = getters[getWorkflow.getter](workflowId);
		const newWorkflowData = { ...workflowData, is_setup_completed: true };
		dispatch(setWorkflowData.action, { data: newWorkflowData });
	},

	async [setAllWorkflowData.action]({ dispatch }, payload) {
		const {
			data: workflowData,
			needFetchNodes,
			forceGetNodes,
			getSubFlowConfigurationParams,
			dontGetConfigurations,
			getWorkflowWaitingFlag,
		} = payload;
		// if currently in route for this workflow, fetch its nodes
		const fetchNodes = needFetchNodes;
		const needToGetNodes = fetchNodes || forceGetNodes;
		await dispatch(setWorkflowData.action, { data: workflowData });
		if (needToGetNodes) {
			await dispatch(getWorkflowVersionGraph.action, { workflowId: workflowData.id, workflowVersionId: workflowData.current_draft.id });
		}
		if (workflowData.native_element) {
			dispatch(setNativeElementData.fullPath, { data: workflowData.native_element }, { root: true });
		}
		if (workflowData.current_draft?.ui_element) {
			await dispatch(setUiElementData.fullPath, { data: workflowData.current_draft.ui_element }, { root: true });
		}

		const draftData = workflowData.current_draft;
		if (draftData?.general_config_options) {
			if (isObject(draftData.general_config_options)) {
				dispatch(setConfigurationData.fullPath, { data: draftData.general_config_options }, { root: true });
			} else {
				if (!dontGetConfigurations) {
					await dispatch(getConfiguration.fullPath, { id: draftData.general_config_options, forceGet: true }, { root: true });
				}
			}
		}
		if (draftData?.user_specific_config_options) {
			if (isObject(draftData.user_specific_config_options)) {
				dispatch(setConfigurationData.fullPath, { data: draftData.user_specific_config_options }, { root: true });
			} else {
				if (!dontGetConfigurations) {
					await dispatch(getConfiguration.fullPath, { id: draftData.user_specific_config_options, forceGet: true }, { root: true });
				}
			}
		}
		if (workflowData.installable) {
			await dispatch(getInstallable.fullPath, { id: workflowData.installable, forceGet: true }, { root: true });
		}

		if (WorkflowService.isOfTypeSubFlow(workflowData)) {
			if (getWorkflowWaitingFlag) {
				await dispatch(
					fetchIsWaitingForSampleData.fullPath,
					{ id: workflowData.id, waiting_type: WAITING_FLAG_TYPES.SUB_FLOW_SAMPLE },
					{ root: true }
				);
			}
			if (getSubFlowConfigurationParams) {
				if (draftData?.sub_flow_input_configuration) {
					await dispatch(fetchSubflowParams.fullPath, { id: draftData?.sub_flow_input_configuration, forceGet: true }, { root: true });
				}
				if (draftData?.sub_flow_output_configuration) {
					await dispatch(fetchSubflowParams.fullPath, { id: draftData?.sub_flow_output_configuration, forceGet: true }, { root: true });
				}
				const currentVersion = workflowData.current_version;
				if (currentVersion) {
					const currentVersionData = await dispatch(
						getVersionOfWorkflow.fullPath,
						{
							workflowId: workflowData.id,
							workflowVersionId: currentVersion,
						},
						{ root: true }
					);
					if (currentVersionData?.sub_flow_input_configuration) {
						await dispatch(
							fetchSubflowParams.fullPath,
							{ id: currentVersionData?.sub_flow_input_configuration, forceGet: true },
							{ root: true }
						);
					}
					if (currentVersionData?.sub_flow_output_configuration) {
						await dispatch(
							fetchSubflowParams.fullPath,
							{ id: currentVersionData?.sub_flow_output_configuration, forceGet: true },
							{ root: true }
						);
					}
				}
			}
		}
		return workflowData;
	},
	async [getWorkflows.action]({ commit, dispatch }, payload) {
		const { page, filterBy, autoPaginate } = payload;
		const data = await api.getFlows(page, filterBy, autoPaginate);
		const results = autoPaginate ? data : data.results;
		if (results && filterBy.shared_with_me) {
			let workflows = results;
			if (page > 1) {
				workflows = [...state.sharedWorkflows, ...results];
			}
			commit(SET_SHARED_WORKFLOWS, { workflows });
		}
		await dispatch(setAllWorkflowsData.action, { workflows: results.map((workflow) => ({ data: workflow, dontGetConfigurations: true })) });
		return data;
	},
	async [getWorkflowsForCredentials.action]({ dispatch }, payload) {
		const { cid } = payload;
		const data = await api.getFlowsByCredentials(cid);
		const { results } = data;
		await dispatch(setAllWorkflowsData.action, { workflows: results.map((workflow) => ({ data: workflow })) });
		return data;
	},
	[setAllWorkflowsData.action]({ getters, dispatch }, payload) {
		const { workflows } = payload;
		return Promise.all(
			workflows.map(({ data: workflow, ...conf }) =>
				dispatch(setAllWorkflowData.action, { ...conf, data: Object.assign({}, getters[getWorkflow.getter](workflow.id) || {}, workflow) })
			)
		);
	},
	async [createWorkflow.action]({ dispatch, rootGetters }, payload) {
		const { workflow } = payload;

		if (workflow?.project === ALL_PROJECTS_VIRTUAL_ID || workflow?.project === PROJECTS_SHARED_WITH_ME_VIRTUAL_ID) {
			// should throw!! user should choose where to build
			const firstPersonalProject = ProjectService.getFirstPersonalProject(rootGetters[projectsList.fullPath]);
			workflow.project = firstPersonalProject?.id;
		}

		const ret = await api.createFlow(workflow);
		await dispatch(setAllWorkflowData.action, { data: ret });
		return ret;
	},
	async [createSubflowFromExistingNodes.action](ctx, payload) {
		return await api.createFlowFromExistingNodes(payload);
	},
	async [createModule.action]({ dispatch }, payload) {
		const { module, moduleType } = payload;
		const ret = await api.createModule(module, moduleType);
		await dispatch(setAllWorkflowData.action, { data: ret });
		return ret;
	},
	async [deleteWorkflow.action]({ dispatch, getters, commit }, payload) {
		const { id } = payload;
		const workflow = getters[getWorkflow.getter](id);
		const isExperienceBeingDeleted = workflow.flow_type === FlowType.Experience;
		try {
			commit(SET_UPDATING_WORKFLOW, { [id]: true });
			await api.deleteWorkflow(id);
		} finally {
			commit(SET_UPDATING_WORKFLOW, { [id]: false });
		}
		if (isExperienceBeingDeleted) {
			//if it isExperienceBeingDeleted, re fetch apps workspaces, it might delete an entire category
			dispatch(getAppsWorkspaces.action, { forceGet: true });
		}
		dispatch(deleteWorkflowFromStore.action, { id });
	},
	async [deleteWorkflowFromStore.action]({ commit }, payload) {
		commit(REMOVE_WORKFLOW, { id: payload.id });
	},
	async [getWorkflowVersionGraph.action]({ dispatch, commit }, { workflowId, workflowVersionId }) {
		try {
			commit(SET_IS_LOADING_WORKFLOW_GRAPH, true);
			const workflowVersionGraph = await api.getWorkflowVersionGraph(workflowId, workflowVersionId);
			const { nodes, edges } = workflowVersionGraph;
			// todo-graph: remove this when BE is ready. this is a hack to render loops for now
			const allLoopNodes = nodes.filter((node) => WorkflowNodeService.isLoop(node));
			const sortedLoopNodes = allLoopNodes.toSorted((a, b) => a.id - b.id);
			const allLoopStarts = [];
			const allLoopEnds = [];
			sortedLoopNodes.forEach((node, i) => {
				const isLoopStart = i % 2 === 0;
				if (isLoopStart) {
					allLoopStarts.push(node);
				} else {
					allLoopEnds.push(node);
				}
			});
			const loopConnectorEdges = allLoopStarts.map((loopStart, i) => {
				const loopEnd = allLoopEnds[i];
				return {
					target: loopEnd.id,
					source: loopStart.id,
					type: EdgeTypes.LoopConnector,
					id: `${loopStart.id}=>${loopEnd.id}`,
					workflow_version: loopStart.workflow_version,
					markerStart: {
						type: MarkerType.ArrowClosed,
						width: 20,
						height: 20,
						// raises `react-dom.development.js:798 Error: <marker> attribute orient: Expected angle, "rotate(90deg)".` on render, but works 🤷‍♂️
						orient: 'rotate(90deg)',
					},
				};
			});
			const tempEdges = [...edges, ...loopConnectorEdges];
			//
			await dispatch(bulkSetNodesData.fullPath, { data: nodes }, { root: true });
			await dispatch(setEdgesData.fullPath, { data: tempEdges }, { root: true });
			await dispatch(fetchNodesSideEffectsDataByIds.fullPath, { nodes, workflowVersionId }, { root: true });
		} catch (e) {
			throw new Error(e);
		} finally {
			commit(SET_IS_LOADING_WORKFLOW_GRAPH, false);
		}
	},
	async [publishWorkflow.action]({ dispatch }, payload) {
		const { id, ...flags } = payload;
		await dispatch(setLoading.fullPath, { value: true }, { root: true });
		const ret = await api.publishFlow(id, flags);
		await dispatch(setAllWorkflowData.action, { data: ret });
		ExtensionCommunicationService.announceWorkflow(ret);
		await dispatch(setLoading.fullPath, { value: false }, { root: true });
	},
	async [updateWorkflow.action]({ dispatch }, payload) {
		const { id, data } = payload;
		const ret = await api.updateFlow(id, data);
		return dispatch(setAllWorkflowData.action, { data: ret });
	},
	async [moveWorkflowToOtherProject.action]({ dispatch }, payload) {
		const { workflowId, projectId, oldProjectId } = payload;
		const ret = await api.moveWorkflowToOtherProject(workflowId, projectId);
		dispatch(addWorkflowIdToProject.fullPath, { workflowId, projectId }, { root: true });
		dispatch(removeWorkflowIdFromProject.fullPath, { workflowId, projectId: oldProjectId }, { root: true });
		return dispatch(setAllWorkflowData.action, { data: ret });
	},
	[handleWorkflowUpdated.action]({ dispatch }, payload) {
		const { pk: workflowId, getSubFlowConfigurationParams, getWorkflowWaitingFlag } = payload;
		return dispatch(getWorkflowData.action, {
			id: workflowId,
			forceGet: true,
			forceGetNodes: false,
			getSubFlowConfigurationParams,
			getWorkflowWaitingFlag,
		});
	},
	async [shareWorkflow.action]({ dispatch }, payload) {
		const { users, flowId, emailMessage, groups, isSendMail, role, isOn, inviteUserRole } = payload;
		const emails = users.map((x) => x.email);
		try {
			let sentShareInvitation = [];
			if (emails?.length > 0) {
				const res = await api.shareWorkflow(flowId, emails, role, emailMessage, isSendMail, isOn, inviteUserRole);
				sentShareInvitation = emails;
				if (res.shared_with_users) {
					sentShareInvitation = res.shared_with_users;
				}
			}
			if (groups?.length > 0) {
				await api.shareWorkflowWithGroups(flowId, groups, role, emailMessage, isSendMail, isOn);
			}

			dispatch(handleWorkflowUpdated.action, { pk: flowId });
			dispatch(setWorkflowShareData.action, { id: flowId, force: true });
			dispatch(getAccountMembers.fullPath, { forceGet: true }, { root: true });
			return { success: true, emails: sentShareInvitation, groups };
		} catch (error) {
			return { success: false };
		}
	},
	async [updateWorkflowUserRole.action]({ dispatch }, { flowId, userEmails, role }) {
		if (userEmails) {
			await api.updateWorkflowRoles(flowId, role, [], userEmails);
			dispatch(setWorkflowShareData.action, { id: flowId, force: true });
		}
	},
	async [updateWorkflowGroupRole.action]({ dispatch }, { flowId, groups, role }) {
		if (groups) {
			await api.updateWorkflowRoles(flowId, role, groups, []);
			dispatch(setWorkflowShareData.action, { id: flowId, force: true });
		}
	},
	[addNodeToWorkflowNodes.action]({ commit }, payload) {
		commit(ADD_NODE_TO_WORKFLOW_NODES, payload);
	},
	async [toggleWorkflowIsOnSettings.action]({ dispatch }, payload) {
		const { id: flowId, groupId, userId } = payload;
		await api.toggleWorkflowIsOnSettings(flowId, { groupId, userId });
		dispatch(handleWorkflowUpdated.action, { pk: flowId });
		ExtensionCommunicationService.announceWorkflow({ id: flowId });
		dispatch(setWorkflowShareData.action, { id: flowId, force: true });
	},
	async [changeGroupFeatureVisibility.action]({ dispatch }, payload) {
		const { workflowId, groupId, isOn, applyForCurrent } = payload;
		await api.changeGroupFeatureVisibility(workflowId, groupId, isOn, applyForCurrent);
		dispatch(handleWorkflowUpdated.action, { pk: workflowId });
		ExtensionCommunicationService.announceWorkflow({ id: workflowId });
		dispatch(setWorkflowShareData.action, { id: workflowId, force: true });
	},
	async [applyExistingWorkflowVisibilityToAllGroupMembers.action]({ dispatch }, payload) {
		const { id: flowId, groupId } = payload;
		await api.applyExistingWorkflowVisibilityToAllGroupMembers(flowId, groupId);
		dispatch(handleWorkflowUpdated.action, { pk: flowId });
		dispatch(setWorkflowShareData.action, { id: flowId, force: true });
	},
	async [setWorkflowShareData.action]({ commit, state }, payload) {
		const { id: flowId, force } = payload;
		const needUpdate = !state.sharedData[flowId];
		if (needUpdate || force) {
			commit(SET_WORKFLOW_SHARED_DATA_LOADING, { isLoading: true });
			const ret = await api.getWorkflowSharedData(flowId);
			commit(SET_WORKFLOW_SHARED_DATA, { key: flowId, value: ret });
			commit(SET_WORKFLOW_SHARED_DATA_LOADING, { isLoading: false });
		}
	},

	async [restoreWorkflowVersionAsDraft.action]({ dispatch, getters }, payload) {
		const { workflowId, version, copyConfigurationsResponseFromVersion } = payload;
		const workflowBeforeRestore = getters[getWorkflow.getter](workflowId);
		const workflowDraftNodesBeforeRestore = workflowBeforeRestore.current_draft.nodes;
		await Promise.all(workflowDraftNodesBeforeRestore.map((nodeId) => dispatch(deleteNodeData.fullPath, { id: nodeId }, { root: true })));
		const workflow = await api.restoreWorkflowVersionAsDraft(workflowId, version, copyConfigurationsResponseFromVersion);
		await dispatch(clearEdges.fullPath, null, { root: true });
		await dispatch(setWorkflowData.action, { data: workflow });
		await dispatch(getWorkflowVersionGraph.action, { workflowId: workflow.id, workflowVersionId: workflow.current_draft.id });
		return workflow;
	},

	async [createConfigurationFieldForWorkflow.action]({ dispatch }, payload) {
		const { id, fieldValue } = payload;
		const ret = await api.createConfigurationFieldForWorkflow(id, fieldValue);
		const { workflow, field } = ret;
		await dispatch(setAllWorkflowData.action, { data: workflow });
		return field;
	},
	async [createInstallableForWorkflow.action]({ dispatch, getters }, payload) {
		const { id } = payload;
		const installable = await api.createInstallableForWorkflow(id);
		await dispatch(setInstallable.fullPath, { data: installable }, { root: true });

		// update state of workflow with the new related installable
		const workflow = { ...getters[getWorkflow.getter](installable.workflow), installable: installable.id };
		await dispatch(setWorkflowData.action, { data: workflow });

		return installable;
	},
	[testWorkflow.action](ctx, { id }) {
		return api.testFlow(id);
	},
	async [updateWorkflowIsActive.action]({ dispatch }, { id, isActive }) {
		const ret = await api.updateFlowIsActive(id, isActive);
		ExtensionCommunicationService.announceWorkflow(ret);
		return dispatch(setAllWorkflowData.action, { data: ret, dontGetConfigurations: true });
	},
	async [duplicateWorkflow.action]({ dispatch }, { id, versionId, title }) {
		const ret = await api.duplicateWorkflow(id, versionId, title);
		return dispatch(setAllWorkflowData.action, { data: ret, dontGetConfigurations: true, needFetchNodes: true, forceGetNodes: true });
	},
	async [setWaitingForSampleData.action]({ commit }, { id: workflowId, is_waiting: isWaiting, waiting_type: waitingType }) {
		await api.setFlowWaitingForFlag(workflowId, isWaiting, waitingType);
		commit(SET_WAITING_FOR_SAMPLE_DATA, { workflowId, isWaiting });
	},
	async [fetchIsWaitingForSampleData.action]({ commit }, { id: workflowId, waiting_type: waitingType }) {
		const res = await api.getFlowWaitingForFlag(workflowId, waitingType);
		commit(SET_WAITING_FOR_SAMPLE_DATA, { workflowId, isWaiting: res.waiting_for_sample_data });
		return res.waiting_for_sample_data;
	},
	async [changeWorkflowUserSettings.action]({ dispatch }, { id: workflowId, settings }) {
		await api.changeWorkflowUserSettings(workflowId, settings);
		return dispatch(handleWorkflowUpdated.action, { pk: workflowId });
	},
};

export default actions;
